import { motion } from "framer-motion";
import { Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Editorial password gate — replaces the previous centered-input box with a
 * full-bleed cover backdrop, glass-morphism panel, and animated entry. Reuses
 * the gallery's accent color for the unlock button so each studio's brand
 * shows even before the visitor authenticates.
 */
export default function PremiumPasswordGate({
  gallery, password, setPassword, onSubmit, authenticating,
}) {
  const bg = gallery.cover_image
    ? (gallery.cover_image.startsWith("http") ? gallery.cover_image : `${BACKEND}${gallery.cover_image}`)
    : null;
  const accent = gallery.accent_color || "#C9A961";

  return (
    <div
      className="relative min-h-screen w-full text-white overflow-hidden bg-stone-950"
      data-testid="gallery-password-gate"
    >
      {bg && (
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.2, 0.65, 0.3, 0.9] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bg})` }}
        />
      )}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="text-[10px] tracking-[0.32em] uppercase mb-4 opacity-80">
              {gallery.studio_name || "Private gallery"}
            </div>
            <h1
              className="font-light leading-[0.95] tracking-tight text-5xl sm:text-6xl"
              style={{ fontFamily: "var(--gallery-font-serif, 'Cormorant Garamond', serif)" }}
            >
              {gallery.name}
            </h1>
            {gallery.customer_name && (
              <div className="mt-5 text-xs tracking-[0.22em] uppercase opacity-75">
                For {gallery.customer_name}
              </div>
            )}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              style={{ backgroundColor: accent, transformOrigin: "center" }}
              className="h-px w-20 mx-auto mt-8"
            />
          </div>

          <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <Lock className="w-4 h-4" />
              </div>
              <p className="text-sm opacity-75 mb-6 leading-relaxed max-w-sm">
                This collection is private. Enter the password your photographer shared with you to view it.
              </p>
              <form onSubmit={onSubmit} className="w-full space-y-4" data-testid="gallery-password-form">
                <input
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Gallery password"
                  className="w-full bg-transparent border-b border-white/30 py-3 outline-none focus:border-white text-center placeholder:text-white/40 tracking-wider transition-colors"
                  data-testid="gallery-password-input"
                />
                <button
                  type="submit"
                  disabled={!password || authenticating}
                  style={{ backgroundColor: accent }}
                  className="w-full px-5 h-12 text-stone-900 text-xs uppercase tracking-[0.28em] font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
                  data-testid="gallery-password-submit"
                >
                  {authenticating ? "Unlocking…" : "Enter gallery"}
                </button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
